import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { getEditableAttrs } from "./utils";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronIcon } from "./ChevronIcon";
import { SectionHeader, SectionHeaderProps } from "./SectionHeader";
import { CardAlignable, CardAlignableProps } from "./CardAlignable";
import { Logo, LogoProps } from "./Logo";
import { CardLocations, CardLocationsProps } from "./CardLocations";
import { CardCity, CardCityProps } from "./CardCity";
import { CardParking, CardParkingProps } from "./CardParking";
import {
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { sectionWrapperFull as sectionWrapper } from "./sectionWrappers";

const carouselContainer = tv({
  base: cn("uipm:flex uipm:flex-col", "uipm:gap-[var(--container-title-gap)]"),
});

const headerSection = tv({
  base: cn(
    "uipm:flex uipm:flex-row",
    "uipm:gap-[var(--padding-padding-default)]",
    "uipm:md:grid uipm:md:grid-cols-[1fr_auto] uipm:md:gap-8 uipm:md:items-end uipm:md:justify-between",
  ),
  variants: {
    pageType: {
      default: "uipm:max-lg:pr-[var(--container-margin)]",
      location: "uipm:pr-[var(--container-margin)] uipm:lg:pr-0",
    },
  },
  defaultVariants: {
    pageType: "default",
  },
});

const navigationContainer = tv({
  base: cn(
    "uipm:hidden",
    "uipm:md:flex uipm:md:items-end uipm:md:justify-end",
    "uipm:md:gap-2",
  ),
});

const navigationButton = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center",
    "uipm:w-[48px] uipm:h-[48px] uipm:p-3 uipm:rounded-full",
    "uipm:bg-[var(--surface-surface-secondary)] uipm:text-[var(--typography-colors-text-inverted)]",
    "uipm:transition-opacity uipm:duration-200",
    "uipm:cursor-pointer",
    "disabled:uipm:opacity-50 disabled:uipm:cursor-not-allowed",
    "uipm:hover:opacity-80",
  ),
});

const cardsSection = tv({
  base: cn("uipm:relative"),
  variants: {
    layout: {
      default: "",
      logo: "",
      mosaic: "",
      locations: "",
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

const cardsContainer = tv({
  base: "",
  variants: {
    layout: {
      default: "",
      logo: "",
      mosaic:
        "uipm:lg:overflow-visible uipm:lg:touch-none uipm:lg:pointer-events-none",
      locations: "uipm:lg:overflow-visible",
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

const cardsTrack = tv({
  base: cn(
    "uipm:flex uipm:flex-nowrap uipm:items-stretch",
    "uipm:gap-[var(--container-gutter)]",
  ),
  variants: {
    layout: {
      default: "",
      logo: "",
      mosaic:
        "uipm:lg:grid uipm:lg:grid-cols-3 uipm:lg:auto-rows-auto uipm:lg:gap-[var(--spacing-primitives-24)]",
      locations: "",
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

const cardSlide = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:self-stretch",
    "uipm:flex-[0_0_calc(100%-10%)]", // Mobile: 1 full card + 10% peek
    "uipm:md:flex-[0_0_calc(100%/2.6)]", // Tablet: 2 full + 60% of third (2.6 total)
    "uipm:lg:flex-[0_0_calc(25%-18px)]", // Desktop: 4 cards with proper gap calculation
  ),
  variants: {
    layout: {
      default: "",
      logo: "uipm:flex-[0_0_auto]",
      mosaic:
        "uipm:flex-[0_0_calc(100%-10%)] uipm:md:flex-[0_0_calc(50%-12px)] uipm:lg:pointer-events-auto",
      locations:
        "uipm:flex-[0_0_calc(100%-10%)] uipm:md:flex-[0_0_calc(100%/2.6)] uipm:lg:flex-[0_0_calc(33.333%-16px)]", // Desktop: 3 full cards + partial 4th
    },
    gridPosition: {
      "1": "uipm:lg:row-[1/2] uipm:lg:col-[1/2]", // Row 1, Col 1
      "2": "uipm:lg:row-[1/3] uipm:lg:col-[2/3]", // Rows 1-2, Col 2
      "3": "uipm:lg:row-[1/2] uipm:lg:col-[3/4]", // Row 1, Col 3
      "4": "uipm:lg:row-[2/4] uipm:lg:col-[1/2]", // Rows 2-3, Col 1
      "5": "uipm:lg:row-[3/4] uipm:lg:col-[2/3]", // Row 3, Col 2
      "6": "uipm:lg:row-[2/4] uipm:lg:col-[3/4]", // Rows 2-3, Col 3
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

const indicatorsContainer = tv({
  base: cn(
    "uipm:flex uipm:justify-center uipm:gap-[var(--container-gutter)]",
    "uipm:mt-[var(--margin-margin-default)]",
  ),
});

const indicator = tv({
  base: cn(
    "uipm:transition-all uipm:duration-200 uipm:rounded-lg",
    "uipm:cursor-pointer",
  ),
  variants: {
    active: {
      true: "uipm:w-5 uipm:h-2 uipm:bg-[var(--surface-surface-accent)]",
      false: "uipm:w-2 uipm:h-2 uipm:bg-[var(--surface-surface-secondary)]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export interface CarouselProps
  extends Omit<ComponentProps<"section">, "title"> {
  header?: SectionHeaderProps | null;
  cards?: (
    | CardAlignableProps
    | LogoProps
    | CardLocationsProps
    | CardCityProps
    | CardParkingProps
  )[];
  layout?: "default" | "logo" | "mosaic" | "locations";
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  pageType?: "default" | "location";
  cardAspectRatio?: string;
  onNavigationClick?: (
    direction: "left" | "right",
    slidesInView: number[],
  ) => void;
}

export const Carousel: React.FC<CarouselProps> = ({
  header,
  cards,
  layout = "default",
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  pageType = "default",
  onNavigationClick,
  cardAspectRatio,
  className,
  id,
  ...props
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: "trimSnaps",
  });

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const [slideOpacities, setSlideOpacities] = React.useState<number[]>([]);

  const calculateSlideOpacity = React.useCallback(
    (slideRect: DOMRect, containerRect: DOMRect) => {
      if (window.innerWidth < 780) return 1;

      const maxContainerWidth = pageType === "location" ? 920 : 1440;
      const viewportWidth = window.innerWidth;
      const actualContainerWidth = Math.min(maxContainerWidth, viewportWidth);

      const containerCenter = containerRect.left + containerRect.width / 2;
      const mainContainerLeft = containerCenter - actualContainerWidth / 2;
      const mainContainerRight = containerCenter + actualContainerWidth / 2;

      const minOpacity = 0.1;

      // Card fully inside viewport
      if (
        slideRect.left >= mainContainerLeft &&
        slideRect.right <= mainContainerRight
      ) {
        return 1;
      }

      // Card fully outside viewport
      if (
        slideRect.right < mainContainerLeft ||
        slideRect.left > mainContainerRight
      ) {
        return minOpacity;
      }

      // Card partially visible - calculate visible width
      let visibleWidth = 0;

      if (
        slideRect.left < mainContainerLeft &&
        slideRect.right > mainContainerLeft
      ) {
        // Card exiting left
        visibleWidth = Math.min(
          slideRect.right - mainContainerLeft,
          slideRect.width,
        );
      } else if (
        slideRect.left < mainContainerRight &&
        slideRect.right > mainContainerRight
      ) {
        // Card exiting right
        visibleWidth = Math.min(
          mainContainerRight - slideRect.left,
          slideRect.width,
        );
      }

      const visibilityRatio = Math.max(
        0,
        Math.min(1, visibleWidth / slideRect.width),
      );
      return minOpacity + (1 - minOpacity) * visibilityRatio;
    },
    [pageType],
  );

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      if (onNavigationClick) {
        setTimeout(() => {
          onNavigationClick("left", emblaApi.slidesInView());
        }, 50);
      }
    }
  }, [emblaApi, onNavigationClick]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      if (onNavigationClick) {
        setTimeout(() => {
          onNavigationClick("right", emblaApi.slidesInView());
        }, 50);
      }
    }
  }, [emblaApi, onNavigationClick]);

  const scrollTo = React.useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onSelectAndScroll = React.useCallback(() => {
    if (!emblaApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setScrollSnaps(emblaApi.scrollSnapList());

    const rootNode = emblaApi.rootNode();
    const containerNode = emblaApi.containerNode();
    if (!rootNode || !containerNode) return;

    const containerRect = rootNode.getBoundingClientRect();
    const allSlideNodes = emblaApi.slideNodes();

    const opacities = allSlideNodes.map((slideNode) => {
      if (!slideNode) return 1;

      const slideRect = slideNode.getBoundingClientRect();
      return calculateSlideOpacity(slideRect, containerRect);
    });

    setSlideOpacities(opacities);
  }, [emblaApi, calculateSlideOpacity]);

  React.useEffect(() => {
    if (!emblaApi) return;

    const handleEvent = () => onSelectAndScroll();

    handleEvent();

    emblaApi.on("reInit", handleEvent);
    emblaApi.on("select", handleEvent);
    emblaApi.on("scroll", handleEvent);

    return () => {
      emblaApi.off("reInit", handleEvent);
      emblaApi.off("select", handleEvent);
      emblaApi.off("scroll", handleEvent);
    };
  }, [emblaApi, onSelectAndScroll]);

  const shouldShowCard = (
    card:
      | CardAlignableProps
      | LogoProps
      | CardLocationsProps
      | CardCityProps
      | CardParkingProps,
  ): boolean => {
    const isLogo =
      "image" in card &&
      "backgroundColor" in card &&
      !("link" in card) &&
      !("button" in card) &&
      !("links" in card) &&
      !("icon" in card);
    if (isLogo) return true;

    const isAlignable = "icon" in card || "aligning" in card;
    if (isAlignable) return true;

    const hasLinkHref = "link" in card && Boolean(card.link?.href);
    const hasButtonHref = "button" in card && Boolean(card.button?.href);

    return hasLinkHref || hasButtonHref;
  };

  const filteredCards = React.useMemo(() => {
    if (!cards) return [];
    return cards.filter(shouldShowCard);
  }, [cards]);

  const hasCards = filteredCards.length > 0;
  const isMosaicLayout = layout === "mosaic";

  // For mosaic layout, apply default title styles (h5, balanced, two-tone)
  // For location pages, always apply balanced text wrapping
  const enhancedHeader = React.useMemo(() => {
    if (!header) return header;

    if (isMosaicLayout) {
      return {
        ...header,
        title: header.title
          ? {
              ...header.title,
              fontSize: header.title.fontSize || ("h5" as const),
              textWrap: "balance" as const,
              twoToneColor: header.title.twoToneColor || ("secondary" as const),
            }
          : header.title,
      };
    }

    // For location pages, always apply balanced text wrapping
    if (pageType === "location") {
      return {
        ...header,
        title: header.title
          ? {
              ...header.title,
              textWrap: "balance" as const,
            }
          : header.title,
      };
    }

    return header;
  }, [isMosaicLayout, pageType, header]);

  // Use 3:2 aspect ratio for cards on location pages
  const effectiveAspectRatio =
    pageType === "location" ? "3/2" : cardAspectRatio;

  if (!header && !hasCards) return null;

  return (
    <section
      id={id}
      className={sectionWrapper({
        backgroundColor,
        topPadding,
        bottomPadding,
        pageType,
      })}
      {...props}
    >
      <div
        className={cn(
          "uipm:mx-auto uipm:pl-[var(--container-margin)]",
          pageType === "location" || isMosaicLayout
            ? "uipm:max-w-[920px]"
            : "uipm:max-w-[1440px]",
          pageType === "location"
            ? "uipm:lg:px-0"
            : "uipm:lg:px-[var(--container-margin)]",
        )}
      >
        <div className={cn(carouselContainer(), className)}>
          {/* Header Section with Navigation */}
          {enhancedHeader || scrollSnaps.length > 1 ? (
            <div className={headerSection({ pageType })}>
              {enhancedHeader && (
                <div className="uipm:flex-1">
                  <SectionHeader {...enhancedHeader} />
                </div>
              )}

              {/* Navigation buttons for tablet/desktop - hidden on desktop for mosaic layout */}
              {scrollSnaps.length > 1 && (
                <div
                  className={cn(
                    navigationContainer(),
                    isMosaicLayout && "uipm:md:flex uipm:lg:hidden",
                  )}
                >
                  <button
                    className={navigationButton()}
                    onClick={scrollPrev}
                    disabled={!canScrollPrev}
                    aria-label="Previous"
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6 uipm:rotate-180"
                      color="var(--color-color-accent)"
                    />
                  </button>
                  <button
                    className={navigationButton()}
                    onClick={scrollNext}
                    disabled={!canScrollNext}
                    aria-label="Next"
                  >
                    <ChevronIcon
                      className="uipm:w-6 uipm:h-6"
                      color="var(--color-color-accent)"
                    />
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {/* Cards Section */}
          {hasCards && (
            <div className={cardsSection({ layout })}>
              {/* Carousel Container */}
              <div className={cardsContainer({ layout })} ref={emblaRef}>
                <div className={cardsTrack({ layout })}>
                  {(isMosaicLayout
                    ? filteredCards.slice(0, 6)
                    : filteredCards
                  ).map((card, index) => {
                    // Determine card type by checking for unique properties
                    const isLocations = "tariff" in card && "text" in card;
                    const isParking = "tariff" in card && "features" in card;
                    const isCity =
                      "title" in card &&
                      !("tariff" in card) &&
                      !("icon" in card) &&
                      !("text" in card);
                    const isLogo =
                      layout === "logo" ||
                      ("image" in card &&
                        !isLocations &&
                        !isParking &&
                        !isCity &&
                        !("icon" in card));

                    // Calculate grid position for mosaic layout (repeating pattern of 6)
                    const positionInPattern = (index % 6) + 1;
                    const gridPosition = isMosaicLayout
                      ? (positionInPattern.toString() as
                          | "1"
                          | "2"
                          | "3"
                          | "4"
                          | "5"
                          | "6")
                      : undefined;

                    // Calculate row offset for cards beyond the first 6
                    const batchNumber = Math.floor(index / 6);
                    const rowOffset = batchNumber * 3; // Each batch of 6 uses 3 rows

                    // Define grid row/col for each position in the pattern
                    const mosaicGridStyles =
                      isMosaicLayout && gridPosition
                        ? {
                            "1": {
                              gridRow: `${1 + rowOffset}/${2 + rowOffset}`,
                              gridColumn: "1/2",
                            },
                            "2": {
                              gridRow: `${1 + rowOffset}/${3 + rowOffset}`,
                              gridColumn: "2/3",
                            },
                            "3": {
                              gridRow: `${1 + rowOffset}/${2 + rowOffset}`,
                              gridColumn: "3/4",
                            },
                            "4": {
                              gridRow: `${2 + rowOffset}/${4 + rowOffset}`,
                              gridColumn: "1/2",
                            },
                            "5": {
                              gridRow: `${3 + rowOffset}/${4 + rowOffset}`,
                              gridColumn: "2/3",
                            },
                            "6": {
                              gridRow: `${2 + rowOffset}/${4 + rowOffset}`,
                              gridColumn: "3/4",
                            },
                          }[gridPosition]
                        : undefined;

                    const isLastCard = index === filteredCards.length - 1;
                    const opacity = slideOpacities[index] ?? 1;

                    return (
                      <div
                        key={index}
                        className={cn(
                          cardSlide({ layout, gridPosition }),
                          isLastCard &&
                            pageType === "default" &&
                            "uipm:pr-[var(--container-margin)] uipm:lg:pr-0",
                        )}
                        style={{ opacity, ...mosaicGridStyles }}
                      >
                        <div
                          className="uipm:h-full"
                          {...getEditableAttrs(card)}
                        >
                          {isLogo ? (
                            <Logo {...(card as LogoProps)} />
                          ) : isParking ? (
                            <CardParking
                              {...(card as CardParkingProps)}
                              className={
                                isMosaicLayout && gridPosition
                                  ? ["2", "4", "6"].includes(gridPosition)
                                    ? "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:w-[290px] uipm:lg:h-[416px]"
                                    : "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:w-[290px] uipm:lg:h-[290px]"
                                  : undefined
                              }
                              aspectRatio={
                                isMosaicLayout &&
                                gridPosition &&
                                ["1", "3", "5"].includes(gridPosition)
                                  ? "square"
                                  : "tall"
                              }
                            />
                          ) : isCity ? (
                            <CardCity
                              {...(card as CardCityProps)}
                              className={cn(
                                isMosaicLayout && gridPosition
                                  ? ["2", "4", "6"].includes(gridPosition)
                                    ? "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:!w-[290px] uipm:lg:!h-[416px]"
                                    : "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:!w-[290px] uipm:lg:!h-[290px]"
                                  : undefined,
                                isMosaicLayout &&
                                  pageType === "location" &&
                                  "uipm:[&_h3]:lg:text-(length:--typography-sizes-headline-6) uipm:[&_h3]:lg:leading-[var(--typography-line-heights-headline-6)] uipm:[&_h3]:lg:tracking-(--typography-letter-spacing-headline-4)",
                              )}
                              aspectRatio={
                                isMosaicLayout &&
                                gridPosition &&
                                ["1", "3", "5"].includes(gridPosition)
                                  ? "square"
                                  : "tall"
                              }
                            />
                          ) : isLocations ? (
                            <CardLocations
                              {...(card as CardLocationsProps)}
                              className={
                                isMosaicLayout && gridPosition
                                  ? ["2", "4", "6"].includes(gridPosition)
                                    ? "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:w-[290px] uipm:lg:h-[416px]"
                                    : "uipm:max-lg:aspect-[2/3]! uipm:lg:!aspect-auto uipm:lg:w-[290px] uipm:lg:h-[290px]"
                                  : undefined
                              }
                              aspectRatio={
                                isMosaicLayout &&
                                gridPosition &&
                                ["1", "3", "5"].includes(gridPosition)
                                  ? "square"
                                  : "tall"
                              }
                            />
                          ) : (
                            <CardAlignable
                              {...(card as CardAlignableProps)}
                              roundedCorners="always"
                              cardAspectRatio={effectiveAspectRatio}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Indicators - for mosaic layout, show on mobile/tablet only */}
              {hasCards && scrollSnaps.length > 1 && (
                <div
                  className={cn(
                    indicatorsContainer(),
                    isMosaicLayout && "uipm:lg:hidden",
                  )}
                >
                  {scrollSnaps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => scrollTo(index)}
                      className={indicator({ active: index === selectedIndex })}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export type CarouselVariants = VariantProps<typeof carouselContainer>;
